import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import ContactModal from '../components/ContactModal';

const TeamContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #edf2f7 25%, #e2e8f0 50%, #cbd5e0 75%, #f7fafc 100%);
  padding: 6rem 2rem 4rem 2rem;
  color: #1a365d;
  position: relative;

  @media (max-width: 768px) {
    padding: 4rem 1rem 2rem 1rem;
  }
`;

const BackgroundEffects = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 70%, rgba(66, 153, 225, 0.06) 0%, transparent 50%);
  pointer-events: none;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const Title = styled.h1`
  text-align: center;
  font-size: clamp(2.5rem, 6vw, 3.5rem);
  font-weight: 300;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #1a365d, #3182ce, #4299e1);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 2px;
  text-shadow: 0 0 30px rgba(66, 153, 225, 0.3);
`;

const Subtitle = styled.p`
  text-align: center;
  font-size: clamp(1rem, 3vw, 1.2rem);
  color: #718096;
  margin-bottom: 3rem;
  line-height: 1.6;
  padding: 0 1rem;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionToggle = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 4rem;
  flex-wrap: wrap;
`;

const ToggleButton = styled.button<{ isActive: boolean }>`
  background: ${props => props.isActive 
    ? 'linear-gradient(135deg, #3182ce, #4299e1)' 
    : 'linear-gradient(135deg, rgba(66, 153, 225, 0.1), rgba(255, 255, 255, 0.8))'};
  color: ${props => props.isActive ? '#ffffff' : '#3182ce'};
  border: 1px solid ${props => props.isActive ? '#3182ce' : 'rgba(66, 153, 225, 0.3)'};
  padding: 1rem 2rem;
  border-radius: 25px;
  cursor: pointer;
  font-weight: 600;
  font-size: 1rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  min-width: 180px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(66, 153, 225, 0.2);
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

// Keep open positions cards at a consistent card width
const OpeningsGrid = styled(TeamGrid)`
  grid-template-columns: repeat(auto-fit, minmax(320px, 360px));
  justify-content: center;
`;

const MemberCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(66, 153, 225, 0.05));
  border-radius: 20px;
  padding: 2rem;
  border: 1px solid rgba(66, 153, 225, 0.2);
  box-shadow: 0 8px 32px rgba(66, 153, 225, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #3182ce, transparent);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(66, 153, 225, 0.15);
  }
`;

const PhotoPlaceholder = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(99, 179, 237, 0.2), rgba(144, 205, 244, 0.1));
  border: 3px dashed rgba(66, 153, 225, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem auto;
  color: #718096;
  font-size: 0.85rem;
  text-align: center;
  line-height: 1.3;
`;

const Photo = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  display: block;
  margin: 0 auto 1.5rem auto;
  border: 3px solid rgba(66, 153, 225, 0.2);
  background: #fff;
`;

const MemberName = styled.h3`
  text-align: center;
  font-size: 1.5rem;
  color: #3182ce;
  margin-bottom: 0.5rem;
  font-weight: 300;
`;

const MemberRole = styled.p`
  text-align: center;
  color: #718096;
  font-size: 1rem;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
`;

const MemberBio = styled.p`
  color: #4a5568;
  line-height: 1.6;
  font-size: 0.95rem;
  margin-bottom: 1.5rem;
`;

const SkillsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const Skill = styled.span`
  background: rgba(66, 153, 225, 0.1);
  color: #3182ce;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const AvailableCard = styled(MemberCard)`
  border: 2px solid rgba(34, 197, 94, 0.3);
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.05), rgba(255, 255, 255, 0.9));

  &::before {
    background: linear-gradient(90deg, transparent, #22c55e, transparent);
  }
`;

const AvailableBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const RequiredSkills = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(34, 197, 94, 0.05);
  border-radius: 10px;
  border-left: 3px solid #22c55e;
`;

const RequiredTitle = styled.p`
  font-weight: 600;
  color: #16a34a;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
`;


const PhotoCaption = styled.div`
  text-align: center;
  font-size: 0.85rem;
  color: #718096;
  margin-bottom: 1rem;
  line-height: 1.3;
`;
const ApplyButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 15px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(34, 197, 94, 0.3);
  }
`;

const Team: React.FC = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<'current' | 'openings'>('current');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'contact' | 'apply' | 'partner' | 'book'>('contact');

  const ImageWithFallback: React.FC<{ name: string }> = ({ name }) => {
    const candidates = useMemo(() => {
      const base = name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\./g, '')
        .replace(/'/g, '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-');
      const bases = [base, base.replace(/-/g, '_')];
      const exts = ['webp', 'jpg', 'jpeg', 'png'];
      const urls: string[] = [];
      for (const b of bases) {
        for (const ext of exts) {
          urls.push(`/images/team/${b}.${ext}`);
        }
      }
      return urls;
    }, [name]);

    const [idx, setIdx] = useState(0);
    const hasMore = idx < candidates.length;
    if (!hasMore) {
      return (
        <PhotoPlaceholder>
          👤<br />Photo<br />placeholder
        </PhotoPlaceholder>
      );
    }
    return (
      <Photo
        src={candidates[idx]}
        alt={name}
        onError={() => setIdx((i) => i + 1)}
      />
    );
  };

  const openModal = (type: 'contact' | 'apply' | 'partner' | 'book') => {
    setModalType(type);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const currentTeam: Array<{
    name: string;
    role: string;
    bio: string;
    skills: string[];
    photo?: string;
    caption?: string;
  }> = [
    {
      name: 'Yury Hard',
      role: 'Skipper & Navigator',
      bio: '20+ years of ocean sailing experience including long offshore runs. Offshore skipper with stong survival skills. Keen adventurer and wild nature admirer.',
      skills: ['Navigation', 'Leadership', 'Weather Routing', 'Emergency Response','Survival Training']

    },
    {
      name: 'Vadim Stojan',
      role: 'First Mate & Engineer',
      bio: 'Marine engineer with expertise in electrical and mechanical yacht systems and maintenance. Offshore skipper. Keen sail racer.',
      skills: ['Engineering', 'Maintenance', 'Safety Systems', 'Radio Operations','Electronics repair', 'Satellite systems']
    },
    {
      name: 'Tatiana Sulimova',
      role: 'Content Creator & Filmmaker',
      bio: 'Many years of experience in PR, journalism, and event management. Creation of PR materials: press releases, articles, media comments, and materials expedition media.',
      skills: ['Video production', 'Photography', 'Editing', 'Social media', 'Storytelling', 'SMM','']
    },
    
  ];

  const openPositions = [
    // {
    //   role: 'Cook & Provisioning Specialist',
    //   description: 'Experienced yacht cook needed for meal planning, provisioning, and cooking for 6-8 crew members during long ocean passages.',
    //   requiredSkills: ['Professional cooking', 'Food safety', 'Galley management', 'Nutrition planning'],
    //   duration: 'Full expedition (12 months)',
    //   commitment: 'Full-time crew member'
    // },
    // {
    //   role: 'Electronics & Communications Officer',
    //   description: 'Technical specialist for maintaining satellite communications, navigation electronics, and media equipment throughout the voyage.',
    //   requiredSkills: ['Electronics repair', 'Satellite systems', 'Computer networks', 'Amateur radio'],
    //   duration: 'Flexible segments',
    //   commitment: 'Minimum 3 months'
    // },
    // {
    //   role: 'Expedition Doctor',
    //   bio: 'Emergency medicine physician with wilderness and maritime medical training. Experienced in extreme environment healthcare.',
    //   skills: ['Medical Care', 'Emergency Medicine', 'Survival Training', 'Mental Health'],
    //   commitment: 'Project-based'
    // },
    {
      role: 'Additional Crew Member',
      description: 'Experienced sailor to assist with watch duties, sail handling, and general crew responsibilities. Perfect for qualified sailors seeking adventure.',
      requiredSkills: ['Watch keeping', 'Sail handling', 'Basic maintenance', 'Team player'],
      duration: 'Flexible segments',
      commitment: 'Minimum 3 weeks'
    }
  ];

  return (
    <TeamContainer>
      <BackgroundEffects />
      
      <ContentWrapper>
        <Title>{t('team.title')}</Title>
        <Subtitle>
          {t('team.subtitle')}
        </Subtitle>

        <SectionToggle>
          <ToggleButton 
            isActive={activeSection === 'current'}
            onClick={() => setActiveSection('current')}
          >
            {t('team.current_crew')}
          </ToggleButton>
          <ToggleButton 
            isActive={activeSection === 'openings'}
            onClick={() => setActiveSection('openings')}
          >
            {t('team.open_positions')}
          </ToggleButton>
        </SectionToggle>

        {activeSection === 'current' && (
          <TeamGrid>
            {currentTeam.map((member, index) => (
              <MemberCard key={index}>
                <ImageWithFallback name={member.name} />

                <MemberName>{member.name}</MemberName>
                <MemberRole>{member.role}</MemberRole>
                <MemberBio>{member.bio}</MemberBio>
                <SkillsContainer>
                  {member.skills.map((skill, skillIndex) => (
                    <Skill key={skillIndex}>{skill}</Skill>
                  ))}
                </SkillsContainer>
              </MemberCard>
            ))}
          </TeamGrid>
        )}

        {activeSection === 'openings' && (
          <OpeningsGrid>
            {openPositions.map((position, index) => (
              <AvailableCard key={index}>
                <AvailableBadge>Available</AvailableBadge>
                <PhotoPlaceholder>
                  🚢<br/>Position<br/>open
                </PhotoPlaceholder>
                <MemberName>{position.role}</MemberName>
                <MemberRole>{position.commitment}</MemberRole>
                <MemberBio>{position.description}</MemberBio>
                
                <RequiredSkills>
                  <RequiredTitle>Required Skills:</RequiredTitle>
                  <SkillsContainer>
                    {position.requiredSkills.map((skill, skillIndex) => (
                      <Skill key={skillIndex}>{skill}</Skill>
                    ))}
                  </SkillsContainer>
                </RequiredSkills>

                <div style={{ 
                  marginTop: '1rem', 
                  fontSize: '0.9rem', 
                  color: '#4a5568',
                  textAlign: 'center'
                }}>
                  <strong>Duration:</strong> {position.duration}
                </div>

                <ApplyButton onClick={() => openModal('apply')}>
                  {t('team.apply_now')}
                </ApplyButton>
              </AvailableCard>
            ))}
          </OpeningsGrid>
        )}

        {/* Call to Action */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(66, 153, 225, 0.05))',
          padding: '3rem',
          borderRadius: '20px',
          border: '1px solid rgba(66, 153, 225, 0.2)',
          boxShadow: '0 8px 32px rgba(66, 153, 225, 0.1)',
          textAlign: 'center',
          marginTop: '3rem'
        }}>
          <h2 style={{
            fontSize: '2rem',
            color: '#3182ce',
            marginBottom: '1rem',
            fontWeight: 300
          }}>
            Join the Adventure
          </h2>
          <p style={{
            color: '#4a5568',
            fontSize: '1.1rem',
            lineHeight: 1.7,
            marginBottom: '2rem',
            maxWidth: '800px',
            margin: '0 auto 2rem auto'
          }}>
            Arctic Owl is seeking passionate, qualified individuals to join our epic world circumnavigation. 
            Whether you're an experienced sailor, technical specialist, or content creator, we have opportunities 
            for the right people to be part of this once-in-a-lifetime adventure.
          </p>
          
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button 
              onClick={() => openModal('contact')}
              style={{
                background: 'linear-gradient(135deg, #3182ce, #4299e1)',
                color: 'white',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '25px',
                fontWeight: 600,
                fontSize: '1rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              {t('team.contact_us')}
            </button>
          </div>
        </div>
      </ContentWrapper>

      <ContactModal
        isOpen={modalOpen}
        onClose={closeModal}
        type={modalType}
      />
    </TeamContainer>
  );
};

export default Team; 
